import type { ControlSample, KeybindRow, PlayerSlot } from './types';

/** Keyboard maps for same-keyboard local multiplayer. */
const P1_KEYS = {
  left: ['KeyA'],
  right: ['KeyD'],
  fast: ['ShiftLeft'],
  lunge: ['Space', 'KeyW'],
  fleche: ['KeyF'],
  parryHigh: ['KeyQ'],
  parryLow: ['KeyE'],
  riposte: ['KeyR'],
} as const;

const P2_KEYS = {
  left: ['ArrowLeft'],
  right: ['ArrowRight'],
  fast: ['ShiftRight'],
  lunge: ['ArrowUp', 'Numpad0', 'Enter'],
  fleche: ['ArrowDown', 'NumpadDecimal', 'Slash'],
  parryHigh: ['Numpad1', 'KeyU', 'Comma'],
  parryLow: ['Numpad3', 'KeyO', 'Period'],
  riposte: ['Numpad2', 'KeyI', 'Semicolon'],
} as const;

export const KEYBIND_TABLE: KeybindRow[] = [
  { action: 'Advance', p1: 'D', p2: '→', pad: 'Stick → · Touch stick →' },
  { action: 'Retreat', p1: 'A', p2: '←', pad: 'Stick ← · Touch stick ←' },
  { action: 'Fast step', p1: 'L-Shift', p2: 'R-Shift', pad: 'Stick fully · Touch outer rim' },
  { action: 'Lunge', p1: 'Space / W', p2: '↑ / Enter', pad: 'A / × · LUNGE' },
  { action: 'Attack (mouse)', p1: 'Left click', p2: '—', pad: 'A / ×' },
  { action: 'Parry (mouse)', p1: 'Right click', p2: '—', pad: 'LB/RB' },
  { action: 'Flèche', p1: 'F', p2: '↓ / /', pad: 'B / ○ · FLÈCHE' },
  { action: 'Parry high', p1: 'Q', p2: 'U / ,', pad: 'LB · PARRY HI' },
  { action: 'Parry low', p1: 'E', p2: 'O / .', pad: 'RB · PARRY LO' },
  { action: 'Riposte', p1: 'R', p2: 'I / ;', pad: 'X / □ · RIP' },
  { action: 'Aim sword', p1: 'Mouse move', p2: '—', pad: 'Right stick' },
  { action: 'Lock mouse', p1: 'Click canvas', p2: '—', pad: '—' },
  { action: 'Cameras', p1: '1 / 2 / 3 / C', p2: 'shared', pad: 'D-pad / Y' },
  { action: 'Pause', p1: 'Esc / P', p2: 'Esc / P', pad: 'Start · Ⅱ' },
  { action: 'Anim debug', p1: 'F2 / `', p2: 'shared', pad: '—' },
];

export class Input {
  private keys = new Set<string>();
  private just = new Set<string>();
  private justUp = new Set<string>();
  pointerLocked = false;
  mouseDX = 0;
  mouseDY = 0;
  /** Absolute mouse aim when not pointer-locked (normalized -1..1). */
  mouseNX = 0;
  mouseNY = 0;
  private wantLock = false;
  private mouseJust: Set<number> = new Set();
  private mouseDown: Set<number> = new Set();

  /** Connected gamepad indices (browser Gamepad API). */
  private pads: (Gamepad | null)[] = [];
  private prevPadButtons: boolean[][] = [[], []];

  constructor(private canvas: HTMLElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.clear);
    window.addEventListener('gamepadconnected', this.onPadConnect);
    window.addEventListener('gamepaddisconnected', this.onPadDisconnect);
    canvas.addEventListener('click', () => this.tryPointerLock());
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === canvas;
    });
    document.addEventListener('mousemove', this.onMouse);
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  private isTouch(): boolean {
    return (
      window.matchMedia('(pointer: coarse)').matches ||
      navigator.maxTouchPoints > 0 ||
      'ontouchstart' in window
    );
  }

  /** Request pointer lock for free-look blade aim (desktop 3rd/FPS). */
  setWantPointerLock(want: boolean): void {
    this.wantLock = want;
    if (!want) {
      if (document.pointerLockElement) document.exitPointerLock?.();
      return;
    }
    this.tryPointerLock();
  }

  tryPointerLock(): void {
    if (!this.wantLock || this.isTouch()) return;
    if (document.pointerLockElement !== this.canvas) {
      this.canvas.requestPointerLock?.();
    }
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.clear);
    window.removeEventListener('gamepadconnected', this.onPadConnect);
    window.removeEventListener('gamepaddisconnected', this.onPadDisconnect);
    document.removeEventListener('mousemove', this.onMouse);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mouseup', this.onMouseUp);
  }

  private onMouseDown = (e: MouseEvent): void => {
    // 0 = LMB, 2 = RMB
    if (!this.mouseDown.has(e.button)) this.mouseJust.add(e.button);
    this.mouseDown.add(e.button);
    if (e.button === 2) e.preventDefault();
  };

  private onMouseUp = (e: MouseEvent): void => {
    this.mouseDown.delete(e.button);
  };

  /** Left mouse button just pressed — attack */
  mouseAttack(): boolean {
    return this.mouseJust.has(0);
  }

  /** Right mouse button just pressed — parry */
  mouseParry(): boolean {
    return this.mouseJust.has(2);
  }

  private onPadConnect = (): void => {
    this.refreshPads();
  };

  private onPadDisconnect = (): void => {
    this.refreshPads();
  };

  private refreshPads(): void {
    this.pads = Array.from(navigator.getGamepads?.() ?? []);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const k = e.code;
    if (!this.keys.has(k)) this.just.add(k);
    this.keys.add(k);
    if (
      [
        'Space',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Slash',
        'Numpad0',
        'Numpad1',
        'Numpad2',
        'Numpad3',
      ].includes(k)
    ) {
      e.preventDefault();
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
    this.justUp.add(e.code);
  };

  private onMouse = (e: MouseEvent): void => {
    if (this.pointerLocked) {
      this.mouseDX += e.movementX;
      this.mouseDY += e.movementY;
      return;
    }
    // Unlocked: map cursor position on canvas to aim (-1..1)
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    this.mouseNX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouseNY = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
  };

  private clear = (): void => {
    this.keys.clear();
  };

  endFrame(): void {
    this.just.clear();
    this.justUp.clear();
    this.mouseJust.clear();
    this.mouseDX = 0;
    this.mouseDY = 0;
    // Snapshot pad button states for edge detection next frame
    this.refreshPads();
    for (let i = 0; i < 2; i++) {
      const gp = this.padForSlot(i as PlayerSlot);
      if (!gp) {
        this.prevPadButtons[i] = [];
        continue;
      }
      this.prevPadButtons[i] = gp.buttons.map((b) => b.pressed);
    }
  }

  forceKey(code: string, down: boolean): void {
    if (down) {
      if (!this.keys.has(code)) this.just.add(code);
      this.keys.add(code);
    } else {
      this.keys.delete(code);
      this.justUp.add(code);
    }
  }

  forceTap(code: string): void {
    this.just.add(code);
  }

  down(code: string): boolean {
    return this.keys.has(code);
  }

  pressed(code: string): boolean {
    return this.just.has(code);
  }

  private anyDown(codes: readonly string[]): boolean {
    return codes.some((c) => this.down(c));
  }

  private anyPressed(codes: readonly string[]): boolean {
    return codes.some((c) => this.pressed(c));
  }

  /** Prefer pad index = slot when present; else first available for P1 only. */
  private padForSlot(slot: PlayerSlot): Gamepad | null {
    this.refreshPads();
    const list = this.pads.filter((g) => g && g.connected) as Gamepad[];
    if (list.length === 0) return null;
    // Match by index if possible
    const byIndex = list.find((g) => g.index === slot);
    if (byIndex) return byIndex;
    if (slot === 0) return list[0];
    if (slot === 1 && list.length > 1) return list[1];
    // Second player may share: only use pad 0 for P1 if single controller in 2p
    return null;
  }

  private padButtonPressed(slot: PlayerSlot, index: number): boolean {
    const gp = this.padForSlot(slot);
    if (!gp || !gp.buttons[index]) return false;
    return gp.buttons[index].pressed;
  }

  private padButtonJust(slot: PlayerSlot, index: number): boolean {
    const gp = this.padForSlot(slot);
    if (!gp || !gp.buttons[index]) return false;
    const now = gp.buttons[index].pressed;
    const was = this.prevPadButtons[slot]?.[index] ?? false;
    return now && !was;
  }

  private padAxis(slot: PlayerSlot, axis: number): number {
    const gp = this.padForSlot(slot);
    if (!gp || gp.axes[axis] == null) return 0;
    const v = gp.axes[axis];
    return Math.abs(v) < 0.28 ? 0 : v;
  }

  /** Sample controls for a player slot (keyboard scheme + optional gamepad). */
  sample(slot: PlayerSlot): ControlSample {
    const map = slot === 0 ? P1_KEYS : P2_KEYS;
    let move = 0;
    if (this.anyDown(map.right)) move += 1;
    if (this.anyDown(map.left)) move -= 1;
    if (this.anyDown(map.fast) && move !== 0) move *= 1.35;

    // Gamepad stick / d-pad
    const ax = this.padAxis(slot, 0);
    if (Math.abs(ax) > Math.abs(move) * 0.5 || move === 0) {
      if (ax > 0.28) move = ax > 0.7 ? 1.35 : 1;
      else if (ax < -0.28) move = ax < -0.7 ? -1.35 : -1;
    }
    // D-pad (standard mapping 14 left, 15 right)
    if (this.padButtonPressed(slot, 15)) move = Math.max(move, 1);
    if (this.padButtonPressed(slot, 14)) move = Math.min(move, -1);

    let lunge = this.anyPressed(map.lunge);
    let fleche = this.anyPressed(map.fleche);
    let parry: 0 | 1 | -1 = 0;
    if (this.anyPressed(map.parryHigh)) parry = 1;
    else if (this.anyPressed(map.parryLow)) parry = -1;
    let riposte = this.anyPressed(map.riposte);

    // Standard gamepad: 0=A, 1=B, 2=X, 3=Y, 4=LB, 5=RB
    if (this.padButtonJust(slot, 0)) lunge = true;
    if (this.padButtonJust(slot, 1)) fleche = true;
    if (this.padButtonJust(slot, 2)) riposte = true;
    if (this.padButtonJust(slot, 4)) parry = 1;
    if (this.padButtonJust(slot, 5)) parry = -1;

    return { move, lunge, fleche, parry, riposte };
  }

  /** Legacy single-player helpers (P1). */
  moveAxis(): number {
    return this.sample(0).move;
  }

  lunge(): boolean {
    return this.sample(0).lunge;
  }

  fleche(): boolean {
    return this.sample(0).fleche;
  }

  parry(): 0 | 1 | -1 {
    return this.sample(0).parry;
  }

  riposte(): boolean {
    return this.sample(0).riposte;
  }

  cycleCamera(): boolean {
    return this.pressed('KeyC') || this.padButtonJust(0, 3) || this.padButtonJust(1, 3);
  }

  cameraDigit(): 1 | 2 | 3 | 0 {
    if (this.pressed('Digit1') || this.padButtonJust(0, 12)) return 1;
    if (this.pressed('Digit2') || this.padButtonJust(0, 15)) return 2;
    if (this.pressed('Digit3') || this.padButtonJust(0, 13)) return 3;
    return 0;
  }

  pause(): boolean {
    return (
      this.pressed('Escape') ||
      this.pressed('KeyP') ||
      this.padButtonJust(0, 9) ||
      this.padButtonJust(1, 9)
    );
  }

  gamepadCount(): number {
    this.refreshPads();
    return this.pads.filter((g) => g && g.connected).length;
  }
}
